import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Box,
  Banner,
  Checkbox,
  Spinner,
  Badge,
  Divider,
  TextField,
  Icon,
} from '@shopify/polaris';
import { EmailIcon, CheckIcon, ClipboardIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';
import { UpgradeBanner } from '../components/UpgradeBanner';

interface EmailConfigStatus {
  abandonedCartEnabled: boolean;
  postPurchaseEnabled: boolean;
  verified: boolean;
  senderEmail: string;
}

interface EmailSenderStatus {
  ready: boolean;
  senderEmail: string;
  replyToEmail?: string;
  senderDomain?: string;
  domainVerified: boolean;
}

interface DnsRecord {
  type: string;
  name: string;
  value: string;
}

interface DomainRegistrationResult {
  success: boolean;
  domain: string;
  dnsRecords: DnsRecord[];
  error?: string;
}

interface DomainVerificationResult {
  domain: string;
  status: string;
  dkimVerified: boolean;
  dkimRecords: Array<DnsRecord & { status?: string }>;
  verifiedForSending: boolean;
  overallStatus?: string;
  error?: string;
}

export default function EmailSetupGuidePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, shopInfo } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  
  // Settings state
  const [abandonedCartEnabled, setAbandonedCartEnabled] = useState(false);
  const [postPurchaseEnabled, setPostPurchaseEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Email sender state
  const [senderStatus, setSenderStatus] = useState<EmailSenderStatus | null>(null);
  const [replyToEmail, setReplyToEmail] = useState('');
  const [isSavingReplyTo, setIsSavingReplyTo] = useState(false);
  
  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<{ success: boolean; message: string } | null>(null);

  // Domain verification state
  const [domainInput, setDomainInput] = useState('');
  const [isRegisteringDomain, setIsRegisteringDomain] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
  const [registeredDomain, setRegisteredDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<DomainVerificationResult | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isDeletingDomain, setIsDeletingDomain] = useState(false);

  // Load settings, sender status, and domain status
  useEffect(() => {
    const loadData = async () => {
      if (!shop) return;
      
      try {
        const [settings, sender] = await Promise.all([
          fetchWithAuth('/api/email/settings') as Promise<EmailConfigStatus>,
          fetchWithAuth('/api/email/sender/status') as Promise<EmailSenderStatus>,
        ]);

        setAbandonedCartEnabled(settings.abandonedCartEnabled ?? false);
        setPostPurchaseEnabled(settings.postPurchaseEnabled ?? false);
        
        setSenderStatus(sender);
        setReplyToEmail(sender.replyToEmail ?? '');

        // If domain is already registered, load its verification status
        if (sender.senderDomain) {
          setRegisteredDomain(sender.senderDomain);
          try {
            const domainStatus = await fetchWithAuth(
              `/api/email/sender/domain/status?domain=${encodeURIComponent(sender.senderDomain)}`
            ) as DomainVerificationResult;
            setVerificationStatus(domainStatus);
            // Populate DNS records from status so they show on page load
            if (domainStatus.dkimRecords?.length) {
              setDnsRecords(domainStatus.dkimRecords.map(r => ({
                type: r.type,
                name: r.name,
                value: r.value,
              })));
            }
          } catch {
            // Domain status fetch failed — not critical
          }
        }
      } catch (err) {
        console.error('Error loading email data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [shop, fetchWithAuth]);

  const registerDomain = useCallback(async () => {
    if (!shop || !domainInput.trim()) return;
    
    setIsRegisteringDomain(true);
    setDomainError('');
    setDnsRecords([]);
    
    try {
      const result = await fetchWithAuth('/api/email/sender/domain/register', {
        method: 'POST',
        body: { domain: domainInput.trim() },
      }) as DomainRegistrationResult;
      
      if (result.success) {
        setRegisteredDomain(result.domain);
        setDnsRecords(result.dnsRecords || []);
        setDomainInput('');
      } else {
        setDomainError(result.error || t('email.domainRegisterFailed'));
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err 
        ? (err as { message: string }).message 
        : t('email.domainRegisterFailed');
      setDomainError(msg);
    } finally {
      setIsRegisteringDomain(false);
    }
  }, [shop, domainInput, fetchWithAuth, t]);

  const checkVerification = useCallback(async () => {
    if (!registeredDomain) return;
    
    setIsVerifying(true);
    try {
      const status = await fetchWithAuth(
        `/api/email/sender/domain/status?domain=${encodeURIComponent(registeredDomain)}`
      ) as DomainVerificationResult;
      setVerificationStatus(status);
      // Update DNS records from status
      if (status.dkimRecords?.length) {
        setDnsRecords(status.dkimRecords.map(r => ({
          type: r.type,
          name: r.name,
          value: r.value,
        })));
      }
    } catch (err) {
      console.error('Error checking verification:', err);
    } finally {
      setIsVerifying(false);
    }
  }, [registeredDomain, fetchWithAuth]);

  const deleteDomain = useCallback(async () => {
    if (!registeredDomain) return;
    
    setIsDeletingDomain(true);
    try {
      await fetchWithAuth(`/api/email/sender/domain?domain=${encodeURIComponent(registeredDomain)}`, {
        method: 'DELETE',
      });
      setRegisteredDomain('');
      setDnsRecords([]);
      setVerificationStatus(null);
    } catch (err) {
      console.error('Error deleting domain:', err);
    } finally {
      setIsDeletingDomain(false);
    }
  }, [registeredDomain, fetchWithAuth]);

  const copyToClipboard = useCallback(async (text: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    }
  }, []);

  const saveSettings = useCallback(async () => {
    if (!shop) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await fetchWithAuth('/api/email/settings', {
        method: 'POST',
        body: {
          abandonedCartEnabled,
          postPurchaseEnabled,
        },
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving email settings:', err);
    } finally {
      setIsSaving(false);
    }
  }, [shop, abandonedCartEnabled, postPurchaseEnabled, fetchWithAuth]);

  const saveReplyTo = useCallback(async () => {
    if (!shop) return;
    
    setIsSavingReplyTo(true);
    try {
      await fetchWithAuth('/api/email/sender/reply-to', {
        method: 'POST',
        body: { replyToEmail },
      });
      setSenderStatus(prev => prev ? { ...prev, replyToEmail } : prev);
    } catch (err) {
      console.error('Error saving reply-to:', err);
    } finally {
      setIsSavingReplyTo(false);
    }
  }, [shop, replyToEmail, fetchWithAuth]);

  const sendTestEmail = useCallback(async () => {
    if (!shop || !testEmail) return;
    
    setIsSendingTest(true);
    setTestEmailResult(null);
    
    try {
      const response = await fetchWithAuth('/api/email/sender/test', {
        method: 'POST',
        body: { shop, toEmail: testEmail },
      }) as { success: boolean; message: string; error?: string };
      
      if (response.success) {
        setTestEmailResult({ success: true, message: response.message || t('email.testEmailSent') });
      } else {
        setTestEmailResult({ success: false, message: response.error || response.message || t('email.testEmailFailed') });
      }
    } catch (err: unknown) {
      // Try to extract error message from the response body
      let errorMsg = t('email.testEmailFailed');
      if (err && typeof err === 'object' && 'error' in err) {
        errorMsg = (err as { error: string }).error;
      } else if (err && typeof err === 'object' && 'message' in err) {
        errorMsg = (err as { message: string }).message;
      }
      setTestEmailResult({ success: false, message: errorMsg });
    } finally {
      setIsSendingTest(false);
    }
  }, [shop, testEmail, fetchWithAuth, t]);

  const editTemplate = (templateType: string) => {
    navigate(`/app/email/template/${templateType}`);
  };

  if (isLoading) {
    return (
      <Page title={t('email.title')}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="400">
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text as="p" variant="bodyMd">{t('common.loading')}</Text>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Plan gating
  const hasEmailCampaigns = shopInfo?.planFeatures?.hasEmailCampaigns !== false;
  if (!hasEmailCampaigns) {
    return (
      <Page
        title={t('email.title')}
        backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
      >
        <Layout>
          <Layout.Section>
            <UpgradeBanner feature="emailCampaigns" requiredPlan="Professional" />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const isReady = senderStatus?.ready ?? false;

  return (
    <Page
      title={t('email.title')}
      subtitle={t('email.subtitle')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
      primaryAction={{ content: t('email.saveSettings'), onAction: saveSettings, loading: isSaving, disabled: !isReady }}
    >
      <Layout>
        {/* Save Success Banner */}
        {saveSuccess && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSaveSuccess(false)}>
              {t('email.settingsSaved')}
            </Banner>
          </Layout.Section>
        )}

        {/* Step 1: Email Sender Status & Reply-To */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={EmailIcon} />
                  <Text as="h2" variant="headingMd">{t('email.emailSender')}</Text>
                  {isReady && <Badge tone="success">{t('email.ready')}</Badge>}
                </InlineStack>
                
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t('email.emailSenderDesc')}
                </Text>

                {isReady ? (
                  <BlockStack gap="300">
                    <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                      <BlockStack gap="200">
                        <InlineStack gap="200" blockAlign="center">
                          <Icon source={CheckIcon} tone="success" />
                          <Text as="p" variant="bodyMd" fontWeight="semibold">
                            {senderStatus?.senderEmail}
                          </Text>
                        </InlineStack>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t('email.emailSentFrom')}
                        </Text>
                      </BlockStack>
                    </Box>

                    {/* Reply-To email */}
                    <Divider />
                    <Text as="h3" variant="headingSm">{t('email.replyToEmail')}</Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t('email.replyToEmailHelp')}
                    </Text>
                    <InlineStack gap="200">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label=""
                          labelHidden
                          placeholder={t('email.replyToPlaceholder')}
                          value={replyToEmail}
                          onChange={setReplyToEmail}
                          autoComplete="email"
                        />
                      </div>
                      <Button onClick={saveReplyTo} loading={isSavingReplyTo}>
                        {t('common.save')}
                      </Button>
                    </InlineStack>
                    
                    {/* Test Email */}
                    <Divider />
                    <Text as="h3" variant="headingSm">{t('email.sendTestEmail')}</Text>
                    <InlineStack gap="200">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label=""
                          labelHidden
                          placeholder={t('email.testEmailPlaceholder')}
                          value={testEmail}
                          onChange={setTestEmail}
                          autoComplete="email"
                        />
                      </div>
                      <Button
                        onClick={sendTestEmail}
                        loading={isSendingTest}
                        disabled={!testEmail}
                      >
                        {t('email.sendTest')}
                      </Button>
                    </InlineStack>
                    {testEmailResult && (
                      <Banner tone={testEmailResult.success ? 'success' : 'critical'}>
                        {testEmailResult.message}
                      </Banner>
                    )}
                  </BlockStack>
                ) : (
                  <Banner tone="warning">
                    <Text as="p" variant="bodyMd">
                      {t('email.senderNotConfigured')}
                    </Text>
                  </Banner>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Step 2: Custom Domain Verification */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">{t('email.domainSetup')}</Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t('email.domainSetupDesc')}
                </Text>

                {!registeredDomain ? (
                  <BlockStack gap="300">
                    <InlineStack gap="200">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label=""
                          labelHidden
                          placeholder={t('email.domainPlaceholder')}
                          value={domainInput}
                          onChange={setDomainInput}
                          autoComplete="off"
                        />
                      </div>
                      <Button
                        onClick={registerDomain}
                        loading={isRegisteringDomain}
                        disabled={!domainInput.trim()}
                        variant="primary"
                      >
                        {t('email.registerDomain')}
                      </Button>
                    </InlineStack>
                    {domainError && (
                      <Banner tone="critical">{domainError}</Banner>
                    )}
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <InlineStack gap="200" align="space-between" blockAlign="center">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">{registeredDomain}</Text>
                        {(verificationStatus?.status ?? verificationStatus?.overallStatus) === 'VERIFIED' ? (
                          <Badge tone="success">{t('email.verified')}</Badge>
                        ) : (
                          <Badge tone="attention">{t('email.pending')}</Badge>
                        )}
                      </InlineStack>
                      <InlineStack gap="200">
                        <Button onClick={checkVerification} loading={isVerifying}>
                          {t('email.checkVerification')}
                        </Button>
                        <Button onClick={deleteDomain} loading={isDeletingDomain} tone="critical" variant="plain">
                          {t('email.removeDomain')}
                        </Button>
                      </InlineStack>
                    </InlineStack>

                    {/* DNS Records */}
                    {dnsRecords.length > 0 && (
                      <BlockStack gap="300">
                        <Divider />
                        <Text as="h3" variant="headingSm">{t('email.dnsRecordsTitle')}</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t('email.dnsRecordsDesc')}
                        </Text>
                        <Banner tone="warning">
                          <BlockStack gap="200">
                            <Text as="p" variant="bodySm" fontWeight="semibold">
                              {t('email.dnsTxtWarningTitle')}
                            </Text>
                            <Text as="p" variant="bodySm">
                              {t('email.dnsTxtWarningDesc')}
                            </Text>
                            <Text as="p" variant="bodySm">
                              {t('email.dnsTxtWarningExample')}
                            </Text>
                          </BlockStack>
                        </Banner>
                        {dnsRecords.map((record, idx) => (
                          <Card key={idx}>
                            <Box padding="300">
                              <BlockStack gap="200">
                                <InlineStack gap="200" blockAlign="center">
                                  <Badge>{record.type}</Badge>
                                  {verificationStatus?.dkimRecords?.[idx]?.status && (
                                    <Badge tone={
                                      verificationStatus.dkimRecords[idx].status === 'Verified' ? 'success' : 'attention'
                                    }>
                                      {verificationStatus.dkimRecords[idx].status}
                                    </Badge>
                                  )}
                                </InlineStack>
                                <BlockStack gap="100">
                                  <Text as="p" variant="bodySm" tone="subdued">{t('email.dnsName')}</Text>
                                  <InlineStack gap="200" blockAlign="center">
                                    <Box background="bg-surface-secondary" borderRadius="100" padding="200" minWidth="0">
                                      <Text as="p" variant="bodySm" breakWord>
                                        {record.name}
                                      </Text>
                                    </Box>
                                    <Button
                                      onClick={() => copyToClipboard(record.name, `name-${idx}`)}
                                      icon={copiedField === `name-${idx}` ? CheckIcon : ClipboardIcon}
                                      variant="plain"
                                      accessibilityLabel={t('email.copy')}
                                    />
                                  </InlineStack>
                                </BlockStack>
                                <BlockStack gap="100">
                                  <Text as="p" variant="bodySm" tone="subdued">{t('email.dnsValue')}</Text>
                                  <InlineStack gap="200" blockAlign="center">
                                    <Box background="bg-surface-secondary" borderRadius="100" padding="200" minWidth="0">
                                      <Text as="p" variant="bodySm" breakWord>
                                        {record.value}
                                      </Text>
                                    </Box>
                                    <Button
                                      onClick={() => copyToClipboard(record.value, `value-${idx}`)}
                                      icon={copiedField === `value-${idx}` ? CheckIcon : ClipboardIcon}
                                      variant="plain"
                                      accessibilityLabel={t('email.copy')}
                                    />
                                  </InlineStack>
                                </BlockStack>
                              </BlockStack>
                            </Box>
                          </Card>
                        ))}
                      </BlockStack>
                    )}

                    {/* Verification status details */}
                    {verificationStatus && (verificationStatus.status ?? verificationStatus.overallStatus) === 'VERIFIED' && (
                      <Banner tone="success">
                        <Text as="p" variant="bodyMd">
                          {t('email.domainVerifiedSuccess')}
                        </Text>
                      </Banner>
                    )}
                    {verificationStatus && (verificationStatus.status ?? verificationStatus.overallStatus) === 'PENDING' && (
                      <Banner tone="info">
                        <Text as="p" variant="bodyMd">
                          {t('email.domainVerifyPending')}
                        </Text>
                      </Banner>
                    )}
                    {verificationStatus?.error && (
                      <Banner tone="critical">{verificationStatus.error}</Banner>
                    )}
                  </BlockStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Step 3: Email Types */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="500">
                <Text as="h2" variant="headingMd">
                  {t('email.recommendations')}
                </Text>
                
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t('email.recommendationsDesc')}
                </Text>

                <BlockStack gap="400">
                  <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                    <BlockStack gap="200">
                      <InlineStack gap="300" align="space-between" blockAlign="center">
                        <Checkbox
                          label={
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              🛒 {t('email.abandonedCart')}
                            </Text>
                          }
                          checked={abandonedCartEnabled}
                          onChange={setAbandonedCartEnabled}
                        />
                        <Button onClick={() => editTemplate('abandoned_cart')} variant="plain">
                          {t('email.editTemplate')}
                        </Button>
                      </InlineStack>
                      <Box paddingInlineStart="600">
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t('email.abandonedCartHelp')}
                        </Text>
                      </Box>
                    </BlockStack>
                  </Box>

                  <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                    <BlockStack gap="200">
                      <InlineStack gap="300" align="space-between" blockAlign="center">
                        <Checkbox
                          label={
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              📦 {t('email.postPurchase')}
                            </Text>
                          }
                          checked={postPurchaseEnabled}
                          onChange={setPostPurchaseEnabled}
                        />
                        <Button onClick={() => editTemplate('post_purchase')} variant="plain">
                          {t('email.editTemplate')}
                        </Button>
                      </InlineStack>
                      <Box paddingInlineStart="600">
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t('email.postPurchaseHelp')}
                        </Text>
                      </Box>
                    </BlockStack>
                  </Box>
                </BlockStack>

                {!isReady && (
                  <Banner tone="warning">
                    <Text as="p" variant="bodyMd">
                      {t('email.senderNotConfigured')}
                    </Text>
                  </Banner>
                )}

                <Button
                  onClick={saveSettings}
                  loading={isSaving}
                  variant="primary"
                  disabled={!isReady}
                >
                  {t('email.saveSettings')}
                </Button>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
